import { useMemo } from 'react'
import * as THREE from 'three'
import {
  analisarEnvolvimento,
  definicaoDaFuncao,
  geometriaDoEnvolvimento,
  GEOMETRIA_PLACA_ANCORAGEM,
  type ObjetoTabletop,
  type VetorTabletop,
} from '../../dominio/tabletop'
import { Rotulo3D } from '../componentes/Rotulo3D'

/**
 * Placa de ancoragem e ancoragem por envolvimento, desenhadas a partir das
 * mesmas medidas do domínio (`ancoragem.ts` e `envolvimento.ts`), para os
 * pontos de ancoragem caírem nos furos e no mosquetão.
 */

const COR_PLACA = '#2563eb'
const COR_MOSQUETAO = '#cbd5e1'
const RAIO_FITA_M = 0.009

/** Esfera invisível para facilitar o toque numa peça pequena. */
function AlvoDeToque({ raio }: { raio: number }) {
  return (
    <mesh>
      <sphereGeometry args={[raio, 8, 6]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

/** O contorno da placa, em escudo: estreita em cima, larga e arredondada embaixo, com os furos. */
export function geometriaDaPlacaDeAncoragem(): THREE.BufferGeometry {
  const g = GEOMETRIA_PLACA_ANCORAGEM
  const meia = g.larguraM / 2
  const forma = new THREE.Shape()
  // a borda de baixo (o fundo do arco) passa pela origem
  forma.moveTo(-meia, 0.05)
  forma.lineTo(-0.035, 0.135)
  forma.quadraticCurveTo(0, 0.155, 0.035, 0.135)
  forma.lineTo(meia, 0.05)
  forma.quadraticCurveTo(0, -0.05, -meia, 0.05)
  for (const [x, y] of [g.furoDeCima, ...g.furosDeBaixo]) {
    const furo = new THREE.Path()
    furo.absarc(x, y, g.raioFuroM, 0, Math.PI * 2, false)
    forma.holes.push(furo)
  }
  const geo = new THREE.ExtrudeGeometry(forma, { depth: g.espessuraM, bevelEnabled: false, curveSegments: 20 })
  geo.translate(0, 0, -g.espessuraM / 2)
  return geo
}

export function PlacaAncoragemTabletop() {
  const geometria = useMemo(geometriaDaPlacaDeAncoragem, [])
  return (
    <group>
      <AlvoDeToque raio={0.16} />
      <mesh geometry={geometria}>
        <meshStandardMaterial color={COR_PLACA} metalness={0.55} roughness={0.35} />
      </mesh>
    </group>
  )
}

/** Um trecho reto de fita, de `de` a `para`. */
function TrechoDeFita({ de, para, cor }: { de: VetorTabletop; para: VetorTabletop; cor: string }) {
  const { meio, quaternion, comprimento } = useMemo(() => {
    const d = new THREE.Vector3(para[0] - de[0], para[1] - de[1], para[2] - de[2])
    const comprimento = d.length()
    const quaternion = new THREE.Quaternion()
    if (comprimento > 1e-9) quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize())
    return { meio: [(de[0] + para[0]) / 2, (de[1] + para[1]) / 2, (de[2] + para[2]) / 2] as const, quaternion, comprimento }
  }, [de, para])
  return (
    <mesh position={[meio[0], meio[1], meio[2]]} quaternion={quaternion}>
      <cylinderGeometry args={[RAIO_FITA_M, RAIO_FITA_M, comprimento, 8]} />
      <meshStandardMaterial color={cor} roughness={0.8} />
    </mesh>
  )
}

/** Arco de fita em volta do tronco, do ângulo `de` ao `ate` (medidos a partir do +Z, no plano XZ). */
function ArcoDeFita({ raio, de, ate, y = 0, cor }: { raio: number; de: number; ate: number; y?: number; cor: string }) {
  const geometria = useMemo(() => {
    const pontos = Array.from({ length: 33 }, (_, i) => {
      const a = de + ((ate - de) * i) / 32
      return new THREE.Vector3(raio * Math.sin(a), y, raio * Math.cos(a))
    })
    return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pontos), 48, RAIO_FITA_M, 8, false)
  }, [raio, de, ate, y])
  return (
    <mesh geometry={geometria}>
      <meshStandardMaterial color={cor} roughness={0.8} />
    </mesh>
  )
}

/**
 * Ancoragem por envolvimento: a fita em volta do tronco (que é de outra peça;
 * aqui só a fita), as pernas até o mosquetão e um rótulo com o ângulo e a
 * carga por perna.
 */
export function EnvolvimentoTabletop({ objeto }: { objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'> }) {
  const g = geometriaDoEnvolvimento(objeto)
  const analise = analisarEnvolvimento(objeto)
  const cor = definicaoDaFuncao('fita').cor
  const angulo = (p: VetorTabletop) => Math.atan2(p[0], p[2])
  const rotulo =
    analise.anguloEntrePernasGraus === null
      ? `${analise.nome} · 100% numa perna`
      : `${analise.nome} · ${Math.round(analise.anguloEntrePernasGraus)}° · ${Math.round(analise.fracaoPorPerna * 100)}% por perna`
  return (
    <group>
      <AlvoDeToque raio={g.raioM + 0.12} />
      {g.tipo === 'cesta' && <ArcoDeFita raio={g.raioM} de={angulo(g.saidas[1]!)} ate={2 * Math.PI + angulo(g.saidas[0]!)} cor={cor} />}
      {g.tipo === 'enforcado' && (
        <>
          <ArcoDeFita raio={g.raioM} de={0} ate={2 * Math.PI} cor={cor} />
          {/* o estrangulamento: a fita dobrada sobre ela mesma na frente do tronco */}
          <mesh position={[0, 0, g.raioM]}>
            <boxGeometry args={[0.05, 0.035, 0.02]} />
            <meshStandardMaterial color={cor} roughness={0.8} />
          </mesh>
        </>
      )}
      {g.tipo === 'tres-voltas-duas-pegadas' &&
        [-0.035, 0, 0.035].map((y) => <ArcoDeFita key={y} raio={g.raioM} de={0} ate={2 * Math.PI} y={y} cor={cor} />)}
      {g.saidas.map((s, i) => (
        <TrechoDeFita key={i} de={s} para={g.mestre} cor={cor} />
      ))}
      <mesh position={[g.mestre[0], g.mestre[1], g.mestre[2]]} scale={[1, 1.6, 1]} rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.03, 0.006, 8, 20]} />
        <meshStandardMaterial color={COR_MOSQUETAO} metalness={0.85} roughness={0.28} />
      </mesh>
      <Rotulo3D posicao={[g.mestre[0], g.mestre[1] + 0.25, g.mestre[2]]} texto={rotulo} variante="estrutura" sempreVisivel fator={5} />
    </group>
  )
}
